// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useMemo, useRef, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { documentsApi } from "../api/documents"
import { configApi } from "../api/config"
import type { Document, DocumentType } from "../api/types"
import { formatInTimeZone } from "date-fns-tz"
import { useAuth } from "@umd-dit-ai-workspace/workspaces-component"
import { CloudDownload, Trash2, Folder, FolderPlus, File, Edit2, X, CheckCircle, Eye, ArrowLeft, Zap, GitCompare, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Label,
  Button,
} from "@umd-dit-ai-workspace/shared-ui"
import { AnalysisReport } from "@/components/AnalysisReport"
import { ConfirmDialog } from "@/components/ConfirmDialog"

export default function DocumentsPage() {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [documentType, setDocumentType] = useState<DocumentType>('exam')
  const [folderInput, setFolderInput] = useState<string>('')
  const [isCreatingNewFolder, setIsCreatingNewFolder] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedFolder, setSelectedFolder] = useState<string>('all')
  const [sortColumn, setSortColumn] = useState<'filename' | 'type' | 'size' | 'created_by' | 'uploaded' | 'tokens'>('uploaded')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  
  // Folder management state
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [folderToEdit, setFolderToEdit] = useState<string>('')
  const [newFolderName, setNewFolderName] = useState<string>('')
  const [deleteAction, setDeleteAction] = useState<'delete_documents' | 'orphan_documents'>('orphan_documents')
  
  // Analysis viewing state
  const [viewingAnalysis, setViewingAnalysis] = useState<Document | null>(null)
  const [analysisResult, setAnalysisResult] = useState<any>(null)
  
  // Quick action states
  const [lastUploadedDocId, setLastUploadedDocId] = useState<string | null>(null)
  const [lastUploadWasSingleFile, setLastUploadWasSingleFile] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisProgress, setAnalysisProgress] = useState<string>('')
  
  // Upload progress states
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [uploadingLargeFile, setUploadingLargeFile] = useState<boolean>(false)

  // Collapse states
  const [isFoldersSidebarCollapsed, setIsFoldersSidebarCollapsed] = useState(true)
  const [isDocumentsTableCollapsed, setIsDocumentsTableCollapsed] = useState(true)
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<{ id: string; name: string } | null>(null)

  // Rename document state
  const [showRenameDocumentModal, setShowRenameDocumentModal] = useState(false)
  const [documentToRename, setDocumentToRename] = useState<{ id: string; name: string; folder: string | null; type: DocumentType } | null>(null)
  const [newDocumentName, setNewDocumentName] = useState<string>('')
  const [newDocumentType, setNewDocumentType] = useState<DocumentType>('exam')
  const [newDocumentFolder, setNewDocumentFolder] = useState<string>('')

  // Notification banner state
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null)

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  // Ref for file input to allow clearing
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Get authentication context for org_id and ws_id
  const { selectedOrganizationId, selectedWorkspaceId, userId, user } = useAuth()

  // Query for documents list
  const { data: documents = [], isLoading, error, refetch } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      return documentsApi.getDocuments(selectedOrganizationId, selectedWorkspaceId)
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
    refetchInterval: isAnalyzing ? 2000 : 5000, // Poll faster when analyzing
  })

  // Check if any documents have token estimation in progress
  const hasTokenEstimationInProgress = useMemo(() => {
    return documents.some(doc => 
      doc.token_estimation_status === 'processing' ||
      (doc.file_size_bytes > 4 * 1024 * 1024 && !doc.estimated_token_count)
    )
  }, [documents])

  // Poll more frequently when token estimation is in progress
  useEffect(() => {
    if (hasTokenEstimationInProgress && !isAnalyzing) {
      const interval = setInterval(() => {
        refetch()
      }, 3000) // Poll every 3 seconds for token estimation updates
      
      return () => clearInterval(interval)
    }
  }, [hasTokenEstimationInProgress, isAnalyzing, refetch])

  // Query for workspace config to get default LLM model
  const { data: workspaceConfig } = useQuery({
    queryKey: ['workspace-config', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Organization and workspace must be selected')
      }
      return configApi.getWorkspaceConfig(selectedOrganizationId, selectedWorkspaceId)
    },
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Query for folders list (extract unique folders from documents)
  const folders = useMemo(() => {
    const uniqueFolders = new Set<string>()
    documents.forEach(doc => {
      if (doc.folder) {
        uniqueFolders.add(doc.folder)
      }
    })
    return Array.from(uniqueFolders).sort()
  }, [documents])

  // Upload mutation - now handles multiple files
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!selectedOrganizationId || !selectedWorkspaceId || !userId) {
        throw new Error('Missing required parameters: organization, workspace, or user ID')
      }
      if (!folderInput.trim()) {
        throw new Error('Folder name is required')
      }
      const username = user?.userInfo?.username || user?.name || undefined;
      const normalizedFolder = folderInput.trim().toLowerCase()
      
      // Track if this is a single file upload
      const isSingleFile = files.length === 1;
      
      // Check if any files are large
      const hasLargeFile = files.some(file => file.size / (1024 * 1024) > 4);
      if (hasLargeFile) {
        setUploadingLargeFile(true);
        setUploadProgress(0);
      }
      
      // Upload all files sequentially
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const result = await documentsApi.uploadFile(
            '/documents/upload-file', 
            file, 
            selectedOrganizationId, 
            selectedWorkspaceId, 
            userId,
            documentType, 
            username,
            normalizedFolder,
            (progress) => {
              // Calculate overall progress across all files
              const overallProgress = ((i / files.length) * 100) + (progress / files.length);
              setUploadProgress(Math.round(overallProgress));
            }
          );
          results.push({ file: file.name, success: true, result });
        } catch (error: any) {
          results.push({ file: file.name, success: false, error });
        }
      }
      
      // Check if all uploads failed
      const successCount = results.filter(r => r.success).length;
      if (successCount === 0) {
        // All files failed - throw error to trigger onError handler
        const firstError = results[0]?.error;
        throw firstError || new Error('All file uploads failed');
      }
      
      return { results, isSingleFile };
    },
    onSuccess: async ({ results, isSingleFile }) => {
      // Track whether this was a single file upload
      setLastUploadWasSingleFile(isSingleFile);

      // Capture the id of the document THIS user just uploaded, straight from the
      // upload response — NOT from the shared workspace list, which can contain
      // concurrent uploads by other users (that caused user B's "Analyze" to run
      // on user A's document).
      if (isSingleFile) {
        const firstOk = results.find(r => r.success)
        setLastUploadedDocId(firstOk?.result?.document_id ?? null)
      } else {
        setLastUploadedDocId(null)
      }

      setSelectedFiles([])
      setFolderInput('')
      setIsCreatingNewFolder(false)
      setUploadingLargeFile(false)
      setUploadProgress(0)
      // Clear the file input element
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      
      // Count successes and failures
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      const failedFiles = results.filter(r => !r.success);
      
      // Show notification (only for success or partial success - full failure is handled by onError)
      if (failureCount === 0) {
        setNotification({
          type: 'success',
          message: `Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`
        });
      } else {
        // Partial success
        setNotification({
          type: 'info',
          message: `Uploaded ${successCount} file${successCount > 1 ? 's' : ''}, ${failureCount} failed`
        });
      }
      
      await queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (error: any) => {
      setUploadingLargeFile(false)
      setUploadProgress(0)
      console.error('Upload error:', error)
    }
  })

  // Handle delete with confirmation
  const handleDeleteDocument = (documentId: string) => {
    const doc = documents.find(d => d.document_id === documentId)
    if (doc) {
      setDocumentToDelete({ id: documentId, name: doc.original_filename })
      setDeleteConfirmOpen(true)
    }
  }
  
  const confirmDeleteDocument = () => {
    if (documentToDelete) {
      deleteMutation.mutate(documentToDelete.id)
      setDocumentToDelete(null)
    }
  }

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Missing required parameters: organization or workspace ID')
      }
      return await documentsApi.deleteDocument(documentId, selectedOrganizationId, selectedWorkspaceId)
    },
    onSuccess: () => {
      uploadMutation.reset() // Reset upload mutation to prevent quick actions from reappearing
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      const deletedFileName = documentToDelete?.name
      setDeleteConfirmOpen(false)
      setDocumentToDelete(null)
      setNotification({
        type: 'success',
        message: deletedFileName ? `Document "${deletedFileName}" deleted successfully` : 'Document deleted successfully'
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || error.message || 'Failed to delete document'
      setDeleteConfirmOpen(false)
      setDocumentToDelete(null)
      setNotification({
        type: 'error',
        message: message
      })
    }
  })

  // Rename folder mutation
  const renameFolderMutation = useMutation({
    mutationFn: async ({ oldName, newName }: { oldName: string; newName: string }) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Missing required parameters: organization or workspace ID')
      }
      return await documentsApi.renameFolder(oldName, newName, selectedOrganizationId, selectedWorkspaceId)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setShowRenameModal(false)
      setFolderToEdit('')
      setNewFolderName('')
      setNotification({
        type: 'success',
        message: `Folder renamed successfully! ${data.documents_updated} documents updated.`
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || error.message || 'Failed to rename folder'
      setNotification({
        type: 'error',
        message: message
      })
    }
  })

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: async ({ folderName, action }: { folderName: string; action: 'delete_documents' | 'orphan_documents' }) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Missing required parameters: organization or workspace ID')
      }
      return await documentsApi.deleteFolder(folderName, action, selectedOrganizationId, selectedWorkspaceId)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setShowDeleteModal(false)
      setFolderToEdit('')
      setSelectedFolder('all')
      setCurrentPage(1)
      const count = data.documents_deleted || data.documents_orphaned || 0
      const actionText = data.documents_deleted ? 'deleted' : 'orphaned'
      setNotification({
        type: 'success',
        message: `Folder deleted successfully! ${count} documents ${actionText}.`
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || error.message || 'Failed to delete folder'
      setNotification({
        type: 'error',
        message: message
      })
    }
  })

  // Rename document mutation
  const renameDocumentMutation = useMutation({
    mutationFn: async ({ documentId, newFilename, newDocType, newFolder }: { documentId: string; newFilename: string; newDocType?: DocumentType; newFolder?: string }) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('Missing required parameters: organization or workspace ID')
      }
      return await documentsApi.renameDocument(documentId, newFilename, selectedOrganizationId, selectedWorkspaceId, newDocType, newFolder)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setShowRenameDocumentModal(false)
      setDocumentToRename(null)
      setNewDocumentName('')
      setNewDocumentType('exam')
      setNewDocumentFolder('')
      
      // Build success message
      let message = `Document renamed: "${data.old_filename}" → "${data.new_filename}"`
      if (data.old_document_type && data.new_document_type) {
        message += ` and type changed: "${data.old_document_type}" → "${data.new_document_type}"`
      }
      if (data.old_folder !== undefined && data.new_folder !== undefined) {
        const oldFolderDisplay = data.old_folder || 'No folder'
        const newFolderDisplay = data.new_folder || 'No folder'
        message += ` and moved from "${oldFolderDisplay}" to "${newFolderDisplay}"`
      }
      
      setNotification({
        type: 'success',
        message: message
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || error.message || 'Failed to rename document'
      setNotification({
        type: 'error',
        message: message
      })
    }
  })

  const handleDownloadDocument = async (documentId: string, filename: string) => {
    try {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        alert('Organization and workspace must be selected')
        return
      }
      
      const downloadUrl = await documentsApi.downloadDocument(
        documentId,
        selectedOrganizationId,
        selectedWorkspaceId
      )
      
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Failed to download document:', error)
      alert('Failed to download document. Please try again.')
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setSelectedFiles(Array.from(files))
      // Reset upload mutation to clear any previous errors
      uploadMutation.reset()
    }
  }

  const handleRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, index) => index !== indexToRemove)
      
      // If all files are removed, reset the file input
      if (newFiles.length === 0 && fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      
      return newFiles
    })
  }

  const handleUpload = () => {
    if (selectedFiles.length === 0) return

    // Check for duplicate filenames in the same folder
    const normalizedFolder = folderInput.trim().toLowerCase()
    const duplicates = selectedFiles.filter(file => 
      documents.some(
        doc => doc.original_filename.toLowerCase() === file.name.toLowerCase()
          && doc.folder?.toLowerCase() === normalizedFolder
      )
    )

    if (duplicates.length > 0) {
      // Show duplicate error - user needs to handle it
      return
    }

    // Reset quick action states
    setLastUploadedDocId(null)
    setLastUploadWasSingleFile(false)
    setIsAnalyzing(false)
    setAnalysisProgress('')

    uploadMutation.mutate(selectedFiles)
  }

  // Quick action: Analyze Document
  const handleQuickAnalyze = async () => {
    if (!lastUploadedDocId || !selectedOrganizationId || !selectedWorkspaceId || !userId) return

    setIsAnalyzing(true)
    setAnalysisProgress('Starting analysis...')

    let pollInterval: NodeJS.Timeout | null = null
    let timeoutId: NodeJS.Timeout | null = null

    try {
      const username = user?.userInfo?.username || user?.name || undefined

      // Get the LLM model from workspace config, fallback to 'bedrock_claude_45_sonnet'
      const llmModel = workspaceConfig?.default_llm_model || 'bedrock_claude_45_sonnet'

      // Start analysis with hardcoded unified_analysis_prompt
      await documentsApi.analyzeDocument(
        lastUploadedDocId,
        selectedOrganizationId,
        selectedWorkspaceId,
        userId,
        username,
        'default', // prompt_type
        'general_analysis_prompt', // prompt_name
        'analyzer', // analysis_type
        undefined, // template_type
        undefined, // template_name
        llmModel // llm_model from workspace config
      )

      setAnalysisProgress('Analysis in progress...')

      // Poll for completion
      pollInterval = setInterval(async () => {
        try {
          const docs = await documentsApi.getDocuments(selectedOrganizationId, selectedWorkspaceId)
          const analyzedDoc = docs.find(d => d.document_id === lastUploadedDocId)

          if (analyzedDoc?.processing_status === 'completed' && analyzedDoc.analysis_data) {
            if (pollInterval) clearInterval(pollInterval)
            if (timeoutId) clearTimeout(timeoutId)

            // Show the analysis report directly - no delay
            setViewingAnalysis(analyzedDoc)
            setAnalysisResult({
              status: 'completed',
              filename: analyzedDoc.original_filename,
              document_type: analyzedDoc.document_type,
              duration: 0,
              promptName: analyzedDoc.analysis_data.promptName || analyzedDoc.analysis_data.prompt_name,
              analysisType: analyzedDoc.analysis_data.analysisType || analyzedDoc.analysis_data.analysis_type,
              promptType: analyzedDoc.analysis_data.promptType || analyzedDoc.analysis_data.prompt_type,
              templateName: analyzedDoc.analysis_data.templateName || analyzedDoc.analysis_data.template_name,
              templateType: analyzedDoc.analysis_data.templateType || analyzedDoc.analysis_data.template_type,
              analyzedBy: analyzedDoc.analysis_data.document_metadata?.analyzed_by || analyzedDoc.uploaded_by_username || analyzedDoc.uploaded_by,
              analysisDate: analyzedDoc.analysis_data.document_metadata?.analysis_timestamp,
              ...analyzedDoc.analysis_data
            })

            // Clear the quick action states and reset upload mutation
            setIsAnalyzing(false)
            setLastUploadWasSingleFile(false)
            uploadMutation.reset() // Reset upload mutation to prevent quick actions from reappearing
          } else if (analyzedDoc?.processing_status === 'failed') {
            if (pollInterval) clearInterval(pollInterval)
            if (timeoutId) clearTimeout(timeoutId)
            setIsAnalyzing(false)
            setAnalysisProgress('Analysis failed. Please try again.')
          }
        } catch (error) {
          console.error('Error polling for analysis status:', error)
        }
      }, 3000)

      // Timeout after 5 minutes
      timeoutId = setTimeout(() => {
        if (pollInterval) clearInterval(pollInterval)
        setIsAnalyzing(false)
        setAnalysisProgress('Analysis is taking longer than expected. Check the Analysis tab.')
      }, 300000)

    } catch (error) {
      console.error('Failed to start analysis:', error)
      if (pollInterval) clearInterval(pollInterval)
      if (timeoutId) clearTimeout(timeoutId)
      setIsAnalyzing(false)
      setAnalysisProgress('Failed to start analysis. Please try again.')
    }
  }

  // Get the last uploaded document
  const lastUploadedDoc = lastUploadedDocId
    ? documents.find(d => d.document_id === lastUploadedDocId)
    : null

  // Check if we have enough documents to compare (need at least 2)
  const hasEnoughDocsToCompare = documents.filter(d =>
    d.processing_status === 'uploaded' || d.processing_status === 'completed'
  ).length >= 2

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded': return 'bg-blue-100 text-blue-800'
      case 'processing': return 'bg-yellow-100 text-yellow-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploaded': return 'Ready'
      case 'processing': return 'Processing'
      case 'completed': return 'Analyzed'
      case 'failed': return 'Failed'
      default: return status
    }
  }

  // Get folder counts
  const getFolderCount = (folder: string) => {
    return documents.filter(doc => doc.folder === folder).length
  }

  // Filter documents based on selected folder and search query
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      const matchesFolder = selectedFolder === 'all' || doc.folder === selectedFolder
      const matchesSearch = searchQuery === '' || 
        doc.original_filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.document_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.uploaded_by_username || doc.uploaded_by).toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.folder || '').toLowerCase().includes(searchQuery.toLowerCase())
      return matchesFolder && matchesSearch
    })
  }, [documents, selectedFolder, searchQuery])

  // Sort documents
  const sortedDocuments = useMemo(() => {
    return [...filteredDocuments].sort((a, b) => {
      let aValue: any, bValue: any

      switch (sortColumn) {
        case 'filename':
          aValue = a.original_filename.toLowerCase()
          bValue = b.original_filename.toLowerCase()
          break
        case 'type':
          aValue = a.document_type.toLowerCase()
          bValue = b.document_type.toLowerCase()
          break
        case 'size':
          aValue = a.file_size_bytes
          bValue = b.file_size_bytes
          break
        case 'created_by':
          aValue = (a.uploaded_by_username || a.uploaded_by).toLowerCase()
          bValue = (b.uploaded_by_username || b.uploaded_by).toLowerCase()
          break
        case 'uploaded':
          aValue = new Date(a.uploaded_at).getTime()
          bValue = new Date(b.uploaded_at).getTime()
          break
        case 'tokens':
          aValue = a.estimated_token_count || 0
          bValue = b.estimated_token_count || 0
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredDocuments, sortColumn, sortDirection])

  const handleSort = (column: typeof sortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
      setCurrentPage(1)
    }
  }

  // Pagination logic
  const totalDocuments = sortedDocuments.length
  const totalPages = Math.ceil(totalDocuments / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedDocuments = sortedDocuments.slice(startIndex, endIndex)

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleFolderChange = (folder: string) => {
    setSelectedFolder(folder)
    setCurrentPage(1)
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setCurrentPage(1)
  }

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  const SortIcon = ({ column }: { column: typeof sortColumn }) => {
    if (sortColumn !== column) {
      return <span className="text-blue-600">⇅</span>
    }
    return <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
  }

  // Folder action handlers
  const handleRenameClick = (folder: string) => {
    setFolderToEdit(folder)
    setNewFolderName(folder)
    setShowRenameModal(true)
  }

  const handleDeleteClick = (folder: string) => {
    setFolderToEdit(folder)
    setShowDeleteModal(true)
  }

  const handleRenameSubmit = () => {
    if (!newFolderName.trim()) {
      setNotification({
        type: 'error',
        message: 'Please enter a folder name'
      })
      return
    }
    renameFolderMutation.mutate({
      oldName: folderToEdit,
      newName: newFolderName.trim()
    })
  }

  const handleDeleteSubmit = () => {
    deleteFolderMutation.mutate({
      folderName: folderToEdit,
      action: deleteAction
    })
  }

  // Rename document handlers
  const handleRenameDocumentClick = (doc: Document) => {
    // Extract filename without extension
    const filename = doc.original_filename
    const nameWithoutExt = filename.includes('.') 
      ? filename.substring(0, filename.lastIndexOf('.'))
      : filename
    
    setDocumentToRename({
      id: doc.document_id,
      name: doc.original_filename,
      folder: doc.folder,
      type: doc.document_type
    })
    setNewDocumentName(nameWithoutExt)
    setNewDocumentType(doc.document_type)
    // If document has no folder, default to first available folder
    setNewDocumentFolder(doc.folder || (folders.length > 0 ? folders[0] : ''))
    setShowRenameDocumentModal(true)
  }

  const handleRenameDocumentSubmit = () => {
    if (!newDocumentName.trim()) {
      setNotification({
        type: 'error',
        message: 'Please enter a document name'
      })
      return
    }
    
    if (!documentToRename) return

    // Only pass newDocumentType if it's different from the original
    const newDocType = newDocumentType !== documentToRename.type ? newDocumentType : undefined
    
    // Only pass newFolder if it's different from the original
    const newFolder = newDocumentFolder !== documentToRename.folder ? newDocumentFolder : undefined

    renameDocumentMutation.mutate({
      documentId: documentToRename.id,
      newFilename: newDocumentName.trim(),
      newDocType,
      newFolder
    })
  }

  // Check if folder input creates a new folder
  const isNewFolder = isCreatingNewFolder || (folderInput.trim() && !folders.includes(folderInput.trim().toLowerCase()))

  // Check for duplicate filenames in the same folder (only if folder is provided)
  // Don't check during upload to avoid false positives from race conditions
  const normalizedFolder = folderInput.trim().toLowerCase()
  const duplicateFiles = selectedFiles.length > 0 && folderInput.trim() && !uploadMutation.isPending ? selectedFiles.filter(file =>
    documents.some(
      doc => doc.original_filename.toLowerCase() === file.name.toLowerCase() 
        && doc.folder?.toLowerCase() === normalizedFolder
    )
  ) : []

  // Show message if workspace not selected
  if (!selectedOrganizationId || !selectedWorkspaceId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workspace Required</h2>
          <p className="text-gray-600">Please select an organization and workspace to view documents.</p>
        </div>
      </div>
    )
  }

  return (
    <>
    <ConfirmDialog
      isOpen={deleteConfirmOpen}
      onClose={() => {
        setDeleteConfirmOpen(false)
        setDocumentToDelete(null)
      }}
      onConfirm={confirmDeleteDocument}
      title="Delete Document"
      message={`Are you sure you want to delete "${documentToDelete?.name}"?`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
    />
    
    {/* Notification Banner */}
    {notification && (
      <div className="fixed top-16 left-0 right-0 z-[60] flex justify-center pt-4 px-4 pointer-events-none">
        <div className={`pointer-events-auto max-w-2xl w-full rounded-lg shadow-lg border ${
          notification.type === 'success' 
            ? 'bg-green-50 border-green-200' 
            : notification.type === 'error'
            ? 'bg-red-50 border-red-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="p-4 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {notification.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : notification.type === 'error' ? (
                <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                notification.type === 'success'
                  ? 'text-green-800'
                  : notification.type === 'error'
                  ? 'text-red-800'
                  : 'text-blue-800'
              }`}>
                {notification.message}
              </p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className={`flex-shrink-0 rounded-lg p-1 transition-colors ${
                notification.type === 'success'
                  ? 'hover:bg-green-100 text-green-600'
                  : notification.type === 'error'
                  ? 'hover:bg-red-100 text-red-600'
                  : 'hover:bg-blue-100 text-blue-600'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ${isFoldersSidebarCollapsed ? 'w-12' : 'w-64'}`}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          {!isFoldersSidebarCollapsed && (
            <h2 className="text-lg font-bold text-gray-900">📁 Folders</h2>
          )}
          <button
            onClick={() => setIsFoldersSidebarCollapsed(!isFoldersSidebarCollapsed)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title={isFoldersSidebarCollapsed ? "Expand folders" : "Collapse folders"}
          >
            {isFoldersSidebarCollapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>
        
        {!isFoldersSidebarCollapsed && (
        <div className="flex-1 overflow-y-auto p-2">
          {/* All Documents */}
          <button
            onClick={() => handleFolderChange('all')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors mb-1 ${
              selectedFolder === 'all'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <File className="w-5 h-5" />
            <span className="font-medium flex-1 text-left">All Documents</span>
            <span className="text-sm font-semibold">{documents.length}</span>
          </button>

          {/* Folder List */}
          {folders.length > 0 && (
            <>
              <div className="pt-4 pb-2 px-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Courses
                </h3>
              </div>
              
              {folders.map(folder => (
                <div
                  key={folder}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors mb-1 group ${
                    selectedFolder === folder
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <button
                    onClick={() => handleFolderChange(folder)}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <Folder className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium flex-1 text-left uppercase truncate">{folder}</span>
                    <span className="text-sm font-semibold flex-shrink-0">{getFolderCount(folder)}</span>
                  </button>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRenameClick(folder)
                      }}
                      className="p-1 hover:bg-blue-200 rounded transition-colors"
                      title="Rename folder"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteClick(folder)
                      }}
                      className="p-1 hover:bg-red-200 rounded transition-colors text-red-600"
                      title="Delete folder"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {folders.length === 0 && (
            <div className="px-3 py-8 text-center">
              <FolderPlus className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No folders yet</p>
              <p className="text-xs text-gray-400 mt-1">Upload a document to create your first folder</p>
            </div>
          )}
        </div>
        )}

        {!isFoldersSidebarCollapsed && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600">
            <div className="flex justify-between mb-1">
              <span>Total Folders:</span>
              <span className="font-semibold">{folders.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Documents:</span>
              <span className="font-semibold">{documents.length}</span>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="w-full px-6 py-6 space-y-6">
          {/* Document Upload Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">📤 Document Upload</h2>
            <p className="text-gray-600 mb-2">
              Upload documents to store them in your library. Organize by folder/course for easy access.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              📎 Supported file types: <span className="font-medium text-gray-700">PDF, DOCX, TXT, and PPTX</span>
            </p>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Folder Input */}
                <div>
                  <Label htmlFor="folder-select">
                    📁 Folder/Course <span className="text-red-700">*</span>
                    <span className="sr-only">(required)</span>
                  </Label>

                  {isCreatingNewFolder ? (
                    // Text input mode for creating new folder
                    <div className="relative">
                      <Input
                        id="folder-input"
                        className="uppercase pr-20"
                        placeholder="Enter new folder name..."
                        value={folderInput}
                        onChange={(e) => setFolderInput(e.target.value)}
                        autoFocus
                        aria-describedby={folderInput.trim() ? "folder-feedback" : undefined}
                      />
                      <button
                        onClick={() => {
                          setIsCreatingNewFolder(false)
                          setFolderInput('')
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600 hover:text-gray-900 font-medium"
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    // Dropdown mode for selecting existing folder or creating new
                    <Select
                      value={folderInput}
                      onValueChange={(value) => {
                        if (value === '__create_new__') {
                          setIsCreatingNewFolder(true)
                          setFolderInput('')
                        } else {
                          setFolderInput(value)
                        }
                      }}
                    >
                      <SelectTrigger id="folder-select" className="uppercase" aria-describedby={folderInput.trim() ? "folder-feedback" : undefined}>
                        <SelectValue placeholder={folders.length > 0 ? "Select folder or create new" : "Create new folder"} />
                      </SelectTrigger>
                      <SelectContent>
                        {folders.map(folder => (
                          <SelectItem key={folder} value={folder} className="uppercase">
                            {folder.toUpperCase()}
                          </SelectItem>
                        ))}
                        <SelectItem value="__create_new__" className="text-blue-600 font-medium border-t border-gray-200 mt-1 pt-1">
                          <div className="flex items-center gap-2">
                            <FolderPlus className="w-4 h-4" />
                            <span>Create new folder</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {folderInput.trim() && (
                    <p id="folder-feedback" className="mt-1 text-xs text-gray-500" role="status" aria-live="polite">
                      {isNewFolder ? (
                        <span className="text-green-800 font-medium">
                          ✨ New folder will be created: "{folderInput.trim().toLowerCase()}"
                        </span>
                      ) : (
                        <span className="text-blue-700 font-medium">
                          📂 Using existing folder: "{folderInput.trim().toLowerCase()}"
                        </span>
                      )}
                    </p>
                  )}
                </div>

                {/* Document Type */}
                <div>
                  <Label htmlFor="document-type">Document Type</Label>
                  <Select
                    value={documentType}
                    onValueChange={(value) => setDocumentType(value as DocumentType)}
                  >
                    <SelectTrigger id="document-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exam">Exam</SelectItem>
                      <SelectItem value="syllabus">Syllabus</SelectItem>
                      <SelectItem value="assignment">Assignment</SelectItem>
                      <SelectItem value="quiz">Quiz</SelectItem>
                      <SelectItem value="gradedata">Grade Data</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* File Input */}
                <div>
                  <Label htmlFor="file-input">Select Document(s)</Label>
                  <input
                    ref={fileInputRef}
                    id="file-input"
                    type="file"
                    accept=".pdf,.docx,.txt,.pptx"
                    onChange={handleFileSelect}
                    multiple
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 You can select multiple files to upload at once
                  </p>
                </div>
              </div>

              {selectedFiles.length > 0 && duplicateFiles.length === 0 && (
                <div className="p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-700 mb-2">
                    Ready to upload {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}:
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between text-sm p-2 rounded border border-gray-200">
                        <span className="font-medium text-gray-900 truncate flex-1">{file.name}</span>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <span className="text-gray-600">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="p-1 hover:bg-red-100 rounded transition-colors text-red-600"
                            title="Remove file"
                            aria-label={`Remove ${file.name}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Type: {documentType}
                    {folderInput.trim() && ` | Folder: ${folderInput.trim().toLowerCase()}`}
                  </p>
                  {selectedFiles.some(file => (file.size / 1024 / 1024) > 10) && (
                    <p className="text-sm text-amber-700 mt-2 font-medium">
                      ⚡ Large file(s) detected - Will use direct S3 upload
                    </p>
                  )}
                </div>
              )}

              {selectedFiles.length > 0 && duplicateFiles.length > 0 && (
                <div className="bg-red-50 border border-red-600 rounded p-4">
                  <p className="text-red-800 text-sm font-semibold mb-2">
                    ❌ {duplicateFiles.length} file{duplicateFiles.length > 1 ? 's' : ''} already exist{duplicateFiles.length === 1 ? 's' : ''} in folder "<strong className="uppercase">{folderInput.trim().toLowerCase()}</strong>":
                  </p>
                  <ul className="list-disc list-inside text-red-800 text-sm space-y-1">
                    {duplicateFiles.map((file, index) => (
                      <li key={index}>{file.name}</li>
                    ))}
                  </ul>
                  <p className="text-red-800 text-sm mt-2">
                    Please rename these files or delete the existing files first.
                  </p>
                </div>
              )}

              {/* Error Display for Upload Failures */}
              {uploadMutation.isError && (
                <div className="bg-red-50 border border-red-600 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-800 mb-1">Upload Failed</h3>
                      <p className="text-red-800 text-sm">
                        {(() => {
                          const error = uploadMutation.error as any;
                          // Check if it's a scanned image error (status 422)
                          if (error?.response?.status === 422) {
                            return error?.response?.data?.detail || 'Document contains only scanned images with no extractable text.';
                          }
                          // Other errors
                          return error?.response?.data?.detail || error?.message || 'An unknown error occurred during upload.';
                        })()}
                      </p>
                      {(() => {
                        const error = uploadMutation.error as any;
                        if (error?.response?.status === 422) {
                          return (
                            <div className="mt-3 text-sm text-red-700 bg-red-100 rounded p-3 border border-red-200">
                              <p className="font-medium mb-2">💡 Helpful Tips:</p>
                              <ul className="list-disc list-inside space-y-1 text-xs">
                                <li>If this is a scanned document, use OCR software to convert it to searchable text first</li>
                                <li>Try exporting the document as a text-based PDF instead of an image</li>
                                <li>Ensure your PDF contains actual text content, not just images of text</li>
                              </ul>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="relative group">
                  <button
                    onClick={handleUpload}
                    disabled={selectedFiles.length === 0 || !folderInput.trim() || uploadMutation.isPending || duplicateFiles.length > 0}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                    aria-label={
                      selectedFiles.length === 0 
                        ? "Upload disabled: Please select document(s) first"
                        : !folderInput.trim()
                        ? "Upload disabled: Please select an existing folder or enter a new folder name"
                        : duplicateFiles.length > 0
                        ? "Upload disabled: One or more files already exist in this folder"
                        : "Upload to Library"
                    }
                  >
                    {uploadMutation.isPending ? (
                      <>
                        <div className="animate-spin h-5 w-5 border-3 border-white border-t-transparent rounded-full"></div>
                        {uploadingLargeFile ? `Uploading ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}...` : 'Uploading to Library...'}
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload {selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}` : 'to Library'}
                      </>
                    )}
                  </button>
                  
                  {/* Tooltip - only shows when button is disabled */}
                  {(selectedFiles.length === 0 || !folderInput.trim() || duplicateFiles.length > 0) && !uploadMutation.isPending && (
                    <div 
                      className="invisible group-hover:visible absolute top-full left-0 mt-2 px-4 py-2 bg-gray-100 text-gray-900 text-sm rounded-lg whitespace-nowrap shadow-lg border border-gray-300 z-10"
                      role="tooltip"
                      aria-hidden="true"
                    >
                      {selectedFiles.length === 0 
                        ? "Please select document(s) first"
                        : !folderInput.trim()
                        ? "Please select an existing folder or enter a new folder name"
                        : duplicateFiles.length > 0
                        ? "One or more files already exist in this folder"
                        : ""
                      }
                      <div className="absolute bottom-full left-6 -mb-1 border-4 border-transparent border-b-gray-100"></div>
                    </div>
                  )}
                </div>

              </div>

              {uploadMutation.isSuccess && lastUploadedDoc && !isAnalyzing && lastUploadWasSingleFile && (
                <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="text-gray-900 font-semibold mb-3">
                        ✅ Document uploaded successfully!
                      </p>
                      <p className="text-gray-700 text-sm mb-4">
                        What would you like to do with <strong className="text-gray-900">{lastUploadedDoc.original_filename}</strong>?
                      </p>

                      {/* Quick Action Bubbles */}
                      <div className="flex gap-3 flex-wrap">
                        {/* Analyze Button */}
                        <button
                          onClick={handleQuickAnalyze}
                          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md font-medium"
                        >
                          <Zap className="w-4 h-4" />
                          <span>Analyze Document</span>
                        </button>

                        {/* Compare Button */}
                        <div className="relative group">
                          <button
                            onClick={() => {
                              if (hasEnoughDocsToCompare) {
                                navigate('/comparison')
                              }
                            }}
                            disabled={!hasEnoughDocsToCompare}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all shadow-sm font-medium ${
                              hasEnoughDocsToCompare
                                ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300'
                            }`}
                            aria-label={
                              hasEnoughDocsToCompare
                                ? "Compare Documents"
                                : "Compare Documents (disabled: need more than 1 document to compare)"
                            }
                          >
                            <GitCompare className="w-4 h-4" />
                            <span>Compare Documents</span>
                          </button>

                          {/* Tooltip for disabled state */}
                          {!hasEnoughDocsToCompare && (
                            <div
                              className="invisible group-hover:visible absolute top-full left-0 mt-2 px-4 py-2 bg-gray-100 text-gray-900 text-sm rounded-lg whitespace-nowrap shadow-lg border border-gray-300 z-10"
                              role="tooltip"
                              aria-hidden="true"
                            >
                              Need more than 1 document to compare
                              <div className="absolute bottom-full left-6 -mb-1 border-4 border-transparent border-b-gray-100"></div>
                            </div>
                          )}
                        </div>
                      </div>

                      <p className="text-xs text-gray-600 mt-3">
                        💡 Or continue to the Analysis or Comparison tabs to work with your documents
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isAnalyzing && (
                <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
                  <div className="flex flex-col items-center text-center py-4">
                    <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
                    <p className="text-gray-900 font-semibold mb-2">
                      Analyzing your document...
                    </p>
                    <p className="text-gray-700 text-sm">
                      {analysisProgress}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Document Library */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold">
                  📚 {selectedFolder === 'all' ? 'All Documents' : selectedFolder.toUpperCase()}
                </h2>
                <button
                  onClick={() => setIsDocumentsTableCollapsed(!isDocumentsTableCollapsed)}
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                  title={isDocumentsTableCollapsed ? "Expand documents table" : "Collapse documents table"}
                >
                  {isDocumentsTableCollapsed ? (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
              
              {/* Search Bar */}
              {documents.length > 0 && !isDocumentsTableCollapsed && (
                <div className="relative w-96">
                  <Label htmlFor="document-search" className="sr-only">
                    Search documents
                  </Label>
                  <input
                    id="document-search"
                    type="text"
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searchQuery && (
                    <button
                      onClick={() => handleSearchChange('')}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {!isDocumentsTableCollapsed && (
            <>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500">Loading documents...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 mb-4">Failed to load documents</p>
                <button
                  onClick={() => refetch()}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500 text-lg">No documents in your library yet</p>
                <p className="text-gray-400">Upload your first document above to get started!</p>
              </div>
            ) : sortedDocuments.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No documents found</p>
                <p className="text-gray-400">Try adjusting your search or folder filter</p>
              </div>
            ) : (
              <>
              {/* Pagination Controls - Top */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    {searchQuery ? (
                      <>Found {sortedDocuments.length} of {documents.length} documents</>
                    ) : (
                      <>Showing {startIndex + 1}-{Math.min(endIndex, totalDocuments)} of {totalDocuments} documents</>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Rows per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        onClick={() => handleSort('filename')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Filename
                          <SortIcon column="filename" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Folder
                      </th>
                      <th 
                        onClick={() => handleSort('type')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Type
                          <SortIcon column="type" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        Actions
                      </th>
                      <th 
                        onClick={() => handleSort('created_by')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Uploaded By
                          <SortIcon column="created_by" />
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('tokens')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Token Count
                          <SortIcon column="tokens" />
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('size')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Size
                          <SortIcon column="size" />
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('uploaded')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Uploaded
                          <SortIcon column="uploaded" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedDocuments.map((doc) => {
                      const isAnalyzed = doc.processing_status === 'completed';
                      return (
                      <tr key={doc.document_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-900">{doc.original_filename}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {doc.folder ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium uppercase">
                              {doc.folder}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No folder</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700 capitalize">{doc.document_type}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.processing_status)}`}>
                              {getStatusText(doc.processing_status)}
                            </span>
                            {isAnalyzed && (
                              <CheckCircle className="h-4 w-4 text-green-600" title="Analysis Complete" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {isAnalyzed ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (doc.analysis_data) {
                                      setViewingAnalysis(doc);
                                      setAnalysisResult({
                                        status: 'completed',
                                        filename: doc.original_filename,
                                        document_type: doc.document_type,
                                        duration: 0,
                                        promptName: doc.analysis_data.promptName || doc.analysis_data.prompt_name,
                                        analysisType: doc.analysis_data.analysisType || doc.analysis_data.analysis_type,
                                        promptType: doc.analysis_data.promptType || doc.analysis_data.prompt_type,
                                        templateName: doc.analysis_data.templateName || doc.analysis_data.template_name,
                                        templateType: doc.analysis_data.templateType || doc.analysis_data.template_type,
                                        analyzedBy: doc.analysis_data.document_metadata?.analyzed_by || doc.uploaded_by_username || doc.uploaded_by,
                                        analysisDate: doc.analysis_data.document_metadata?.analysis_timestamp,
                                        ...doc.analysis_data
                                      });
                                    }
                                  }}
                                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                                  title="View analysis report"
                                  aria-label={`View analysis report for ${doc.original_filename}`}
                                >
                                  <Eye className="h-4 w-4 text-blue-600" aria-hidden="true" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDownloadDocument(doc.document_id, doc.original_filename)
                                  }}
                                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                                  title="Download original document"
                                  aria-label={`Download original document: ${doc.original_filename}`}
                                >
                                  <CloudDownload className="h-4 w-4 text-blue-600" aria-hidden="true" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRenameDocumentClick(doc)
                                  }}
                                  className="p-1 hover:bg-yellow-100 rounded transition-colors"
                                  title="Rename document"
                                  aria-label={`Rename document: ${doc.original_filename}`}
                                >
                                  <Edit2 className="h-4 w-4 text-yellow-600" aria-hidden="true" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDocument(doc.document_id)}
                                  disabled={deleteMutation.isPending}
                                  className="p-1 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                                  title="Delete document"
                                  aria-label={`Delete document: ${doc.original_filename}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDownloadDocument(doc.document_id, doc.original_filename)
                                  }}
                                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                                  title="Download original document"
                                  aria-label={`Download original document: ${doc.original_filename}`}
                                >
                                  <CloudDownload className="h-4 w-4 text-blue-600" aria-hidden="true" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRenameDocumentClick(doc)
                                  }}
                                  className="p-1 hover:bg-yellow-100 rounded transition-colors"
                                  title="Rename document"
                                  aria-label={`Rename document: ${doc.original_filename}`}
                                >
                                  <Edit2 className="h-4 w-4 text-yellow-600" aria-hidden="true" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDocument(doc.document_id)}
                                  disabled={deleteMutation.isPending}
                                  className="p-1 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                                  title="Delete document"
                                  aria-label={`Delete document: ${doc.original_filename}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">{doc.uploaded_by_username || doc.uploaded_by}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(doc.token_estimation_status === 'processing' ||
                            (doc.file_size_bytes > 4 * 1024 * 1024 && !doc.estimated_token_count)) ? (
                            <div className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          ) : doc.estimated_token_count ? (
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium font-mono ${
                                doc.estimated_token_count > 50000
                                  ? "bg-red-100 text-red-800 border border-red-300"
                                  : "bg-green-100 text-green-800 border border-green-300"
                              }`}
                              title={`${doc.estimated_token_count.toLocaleString()} tokens (threshold: 50,000)`}
                            >
                              {doc.estimated_token_count.toLocaleString()}
                            </span>
                          ) : doc.token_estimation_status === 'failed' ? (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200" title="Token estimation failed">
                              Failed
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Unknown
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">{(doc.file_size_bytes / 1024 / 1024).toFixed(2)} MB</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {(() => {
                              // Handle both timezone-aware and naive UTC timestamps
                              let dateStr = doc.uploaded_at;
                              // If no timezone info, assume it's UTC and add 'Z'
                              if (!dateStr.includes('+') && !dateStr.endsWith('Z')) {
                                dateStr = dateStr + 'Z';
                              }
                              return formatInTimeZone(new Date(dateStr), 'America/New_York', 'MMM d, yyyy h:mm a');
                            })()}
                          </span>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls - Bottom */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center pt-4 border-t mt-4">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
              </>
            )}
            </>
            )}

            {documents.length > 0 && !isDocumentsTableCollapsed && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-sm">
                  💡 <strong>Next step:</strong> Go to the <strong>Analysis</strong> tab to select and analyze these documents with Claude Sonnet 4.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Analysis Viewing Modal */}
      {viewingAnalysis && analysisResult && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen p-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            <div className="max-w-5xl mx-auto">
              {/* Header with back button */}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      setViewingAnalysis(null);
                      setAnalysisResult(null);
                    }}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Back to Documents</span>
                  </button>
                  <h2 className="text-xl font-bold text-gray-900">
                    Analysis Report: {viewingAnalysis.original_filename}
                  </h2>
                </div>
              </div>

              {/* Analysis Report */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <AnalysisReport analysisData={analysisResult} />
              </div>

              {/* Bottom back button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setViewingAnalysis(null);
                    setAnalysisResult(null);
                  }}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Documents
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Rename Folder</h3>
              <button
                onClick={() => {
                  setShowRenameModal(false)
                  setFolderToEdit('')
                  setNewFolderName('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <Label htmlFor="current-folder">Current Name</Label>
              <Input
                id="current-folder"
                value={folderToEdit.toUpperCase()}
                disabled
                className="bg-gray-100 uppercase"
              />
            </div>

            <div className="mb-6">
              <Label htmlFor="new-folder">New Name</Label>
              <Input
                id="new-folder"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter new folder name..."
                className="uppercase"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameSubmit()
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                This will update all documents in this folder
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowRenameModal(false)
                  setFolderToEdit('')
                  setNewFolderName('')
                }}
                variant="outline"
                disabled={renameFolderMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenameSubmit}
                disabled={renameFolderMutation.isPending || !newFolderName.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {renameFolderMutation.isPending ? 'Renaming...' : 'Rename Folder'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-red-600">Delete Folder</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setFolderToEdit('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete the folder <span className="font-bold uppercase">"{folderToEdit}"</span>?
              </p>
              <p className="text-sm text-gray-600">
                This folder contains {getFolderCount(folderToEdit)} document(s).
              </p>
            </div>

            <div className="mb-6 space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="deleteAction"
                  value="orphan_documents"
                  checked={deleteAction === 'orphan_documents'}
                  onChange={(e) => setDeleteAction(e.target.value as 'orphan_documents')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">Keep Documents</div>
                  <div className="text-sm text-gray-600">
                    Remove folder assignment but keep all documents in the library
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-red-200 rounded-lg cursor-pointer hover:bg-red-50">
                <input
                  type="radio"
                  name="deleteAction"
                  value="delete_documents"
                  checked={deleteAction === 'delete_documents'}
                  onChange={(e) => setDeleteAction(e.target.value as 'delete_documents')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-red-600">Delete All Documents</div>
                  <div className="text-sm text-red-600">
                    Permanently delete the folder and all {getFolderCount(folderToEdit)} document(s)
                  </div>
                </div>
              </label>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowDeleteModal(false)
                  setFolderToEdit('')
                }}
                variant="outline"
                disabled={deleteFolderMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteSubmit}
                disabled={deleteFolderMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteFolderMutation.isPending ? 'Deleting...' : 'Delete Folder'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Document Modal */}
      {showRenameDocumentModal && documentToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Edit Document</h3>
              <button
                onClick={() => {
                  setShowRenameDocumentModal(false)
                  setDocumentToRename(null)
                  setNewDocumentName('')
                  setNewDocumentType('exam')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <Label htmlFor="current-document-name">Current Name</Label>
              <Input
                id="current-document-name"
                value={documentToRename.name}
                disabled
                className="bg-gray-100"
              />
              {documentToRename.folder && (
                <p className="text-xs text-gray-500 mt-1">
                  In folder: <span className="font-medium uppercase">{documentToRename.folder}</span>
                </p>
              )}
            </div>

            <div className="mb-4">
              <Label htmlFor="new-document-name">New Name</Label>
              <Input
                id="new-document-name"
                value={newDocumentName}
                onChange={(e) => setNewDocumentName(e.target.value)}
                placeholder="Enter new document name..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameDocumentSubmit()
                }}
              />
            </div>

            <div className="mb-4">
              <Label htmlFor="new-document-type">Document Type</Label>
              <Select
                value={newDocumentType}
                onValueChange={(value) => setNewDocumentType(value as DocumentType)}
              >
                <SelectTrigger id="new-document-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exam">Exam</SelectItem>
                  <SelectItem value="syllabus">Syllabus</SelectItem>
                  <SelectItem value="assignment">Assignment</SelectItem>
                  <SelectItem value="quiz">Quiz</SelectItem>
                  <SelectItem value="gradedata">Grade Data</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Current type: <span className="font-medium capitalize">{documentToRename.type}</span>
              </p>
            </div>

            <div className="mb-6">
              <Label htmlFor="new-document-folder">Folder</Label>
              <Select
                value={newDocumentFolder}
                onValueChange={(value) => setNewDocumentFolder(value)}
              >
                <SelectTrigger id="new-document-folder" className="uppercase">
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  {folders.map(folder => (
                    <SelectItem key={folder} value={folder} className="uppercase">
                      {folder.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Current folder: <span className="font-medium uppercase">{documentToRename.folder || 'No folder'}</span>
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowRenameDocumentModal(false)
                  setDocumentToRename(null)
                  setNewDocumentName('')
                  setNewDocumentType('exam')
                  setNewDocumentFolder('')
                }}
                variant="outline"
                disabled={renameDocumentMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenameDocumentSubmit}
                disabled={renameDocumentMutation.isPending || !newDocumentName.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {renameDocumentMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}
